import { Box, Chip } from '@mui/material'
import { GridColDef, GridRenderEditCellParams, GridRowId, useGridApiRef } from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import { ReactElement, RefObject, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useParams } from 'react-router'
import { Mutation_Root, Project_Base, Project_Financial_Report } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { usePrompt } from 'src/routing/routing-hooks'
import {
  calculateActualsProjectFundsPercentage,
  MilestoneProjectFundsActuals,
} from 'src/screens/shared/implementation/details/milestone/utils/MilestoneProjectFundsUtils'
import { upsertProjectFinancialReportMutation } from 'src/screens/shared/implementation/details/milestoneQueries'
import { NumberInput } from 'src/shared/form/grid/NumberInput'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { EditableColumnHeader } from 'src/shared/grid/EditableColumnHeader'
import { S } from 'src/shared/styled/S'
import { DataGridUtils } from 'src/shared/utils/DataGridUtils'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useDelayedSubmit } from 'src/shared/utils/hooks/submit-hook'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { muiTheme } from 'src/theme/theme'
import { useClient } from 'urql'

interface RowData {
  id: number
  fundsInCHF: string
  budget: number | null
  approved: number | null
  report: number | null
  totalActuals: number | null
  tooltip: string
  percentage: number | null
}

interface Props {
  mode?: 'view' | 'edit'
  onSave?: () => void
  reportYear?: number
  projectBudgetData: Project_Base | null
  financialReportData: Project_Financial_Report | null
  totalActualsData: MilestoneProjectFundsActuals | null
}

const TOTAL_ROW_ID = 4

export const MilestoneProjectFundsGrid = ({
  mode,
  onSave,
  reportYear,
  projectBudgetData,
  financialReportData,
  totalActualsData,
}: Props): ReactElement => {
  const { milestoneId } = useParams()
  const milestone_id = parseInt(milestoneId as string)

  const [dirty, setDirty] = useState<boolean>(false)
  const [totalData, setTotalData] = useState<{ approved: number; report: number }>()

  const apiRef = useGridApiRef()
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const gridTranslations = useGridTranslateHook()
  const urqlClient = useClient()
  const submit = useDelayedSubmit()

  const calculateTotalData = (value: number, id: GridRowId, field: string, apiRef: RefObject<GridApiPro>): number => {
    const rows =
      apiRef.current
        ?.getAllRowIds()
        .filter((id) => id !== TOTAL_ROW_ID)
        .map((rowId) => ({
          approved:
            field === 'approved' && id === rowId
              ? DataGridUtils.formatFieldValue(value)
              : DataGridUtils.formatFieldValue(apiRef.current?.getRow(rowId).approved),
          report:
            field === 'report' && id === rowId
              ? DataGridUtils.formatFieldValue(value)
              : DataGridUtils.formatFieldValue(apiRef.current?.getRow(rowId).report),
        })) ?? []

    const approvedTotal = DataGridUtils.formatNaNNumber(DataGridUtils.sumBy(rows, 'approved'))
    const reportTotal = DataGridUtils.formatNaNNumber(DataGridUtils.sumBy(rows, 'report'))

    setTotalData({
      approved: approvedTotal,
      report: reportTotal,
    })

    return value
  }

  const projectFundsRows = useMemo(() => {
    const currentRowModels = apiRef.current?.getRowModels?.() ?? new Map()

    const rowData = [
      {
        id: 1,
        fundsInCHF: getMessage('label.gfch.share'),
        approved: projectBudgetData?.project_budget?.gfch_share_approved ?? null,
        report: currentRowModels.get(1)?.report ?? financialReportData?.gfch_share_annual_value ?? null,
        totalActuals: totalActualsData?.gfch_share_annual_value ?? '-',
        percentage:
          calculateActualsProjectFundsPercentage(totalActualsData, totalActualsData?.gfch_share_annual_value ?? 0) ?? 0,
      },
      {
        id: 2,
        fundsInCHF: getMessage('label.share.responsible.organisation'),
        approved: projectBudgetData?.project_budget?.share_responsible_organization_approved ?? null,
        report:
          currentRowModels.get(2)?.report ?? financialReportData?.share_responsible_organization_annual_value ?? null,
        totalActuals: totalActualsData?.share_responsible_organization_annual_value ?? '-',
        percentage:
          calculateActualsProjectFundsPercentage(
            totalActualsData,
            totalActualsData?.share_responsible_organization_annual_value ?? 0,
          ) ?? 0,
      },
      {
        id: 3,
        fundsInCHF: getMessage('label.other.fundings'),
        approved: projectBudgetData?.project_budget?.other_funding_approved ?? null,
        report: currentRowModels.get(3)?.report ?? financialReportData?.other_funding_annual_value ?? null,
        totalActuals: totalActualsData?.other_funding_annual_value ?? '-',
        percentage:
          calculateActualsProjectFundsPercentage(totalActualsData, totalActualsData?.other_funding_annual_value ?? 0) ??
          0,
      },
    ]

    rowData.push({
      id: TOTAL_ROW_ID,
      fundsInCHF: getMessage('label.total'),
      approved: totalData ? totalData.approved : DataGridUtils.sumBy(rowData, 'approved'),
      report: totalData ? totalData.report : DataGridUtils.sumBy(rowData, 'report'),
      totalActuals: rowData.reduce(
        (initial, { totalActuals }) => initial + (parseInt(totalActuals.toString()) || 0),
        0,
      ),
      percentage: 100,
    })

    return rowData as RowData[]
  }, [apiRef, getMessage, projectBudgetData, financialReportData, totalActualsData, totalData])

  const columns = useMemo(() => {
    const hideColumn = mode === 'edit'
    const onRifmChange = (api: GridRenderEditCellParams['api'], id: GridRowId, field: string, value: number) => {
      calculateTotalData(value, id, field, apiRef)
      api.setEditCellValue({ id, field, value })
      setDirty(true)
    }
    return [
      {
        field: 'fundsInCHF',
        headerName: getMessage('label.funds.chf'),
        sortable: false,
        disableColumnMenu: true,
        flex: 1.6,
      },
      {
        field: 'approved',
        headerName: getMessage('label.approved'),
        headerAlign: 'right',
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        flex: 0.3,
        renderCell: ({ value }) => {
          if (value === null || value === '' || Number.isNaN(value)) {
            return '-'
          }
          return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
        },
      },
      {
        field: 'report',
        headerAlign: 'right',
        sortable: false,
        disableColumnMenu: true,
        editable: mode === 'edit',
        align: 'right',
        flex: 0.3,
        renderCell: ({ value }) => {
          if (value) {
            return <S.Span.TextOverflow>{value.toLocaleString('de-CH')}</S.Span.TextOverflow>
          }
          return value === null || Number.isNaN(value) || value === '' ? (
            <S.DataGrid.DefaultSectionTypography $standAlone />
          ) : (
            value
          )
        },
        renderEditCell: (params) => {
          const { value, id, field, api } = params
          const isValid = DataGridUtils.validateValue(value)
          return (
            <NumberInput
              value={value as number}
              onChange={(val: number) => onRifmChange(api, id, field, val)}
              error={!isValid}
            />
          )
        },
        renderHeader: () => (
          <EditableColumnHeader
            headerName={`${getMessage('label.project.funds.report')} ${reportYear ?? ''}`}
            editMode={mode === 'edit'}
          />
        ),
      },
      {
        field: 'totalActuals',
        headerName: getMessage('label.total.actuals'),
        headerAlign: 'right',
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        flex: 0.4,
        hide: hideColumn,
        renderCell: ({ value, row }) => {
          const columnValue =
            value === null || Number.isNaN(value) || value === '' ? (
              '-'
            ) : (
              <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
            )
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: muiTheme.spacing(2), minWidth: 0 }}>
              {columnValue}
              {row?.totalActuals > 0 ? (
                <Chip label={`${row?.percentage}%`} color="secondary" sx={{ width: 70 }} />
              ) : (
                <Chip label={`-`} color="secondary" sx={{ width: 70 }} />
              )}
            </Box>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, apiRef, mode, reportYear])

  usePrompt(getMessage('notification.form.unsaved.changes'), dirty)

  const onSaveLocal = async () => {
    const rows = apiRef.current?.getRowModels()

    if (rows == null) {
      throw new Error('GridApiRef not initialized yet')
    }
    const gfchShareReport = rows.get(1)?.report
    const shareResponsibleOrganizationReport = rows.get(2)?.report
    const otherFundingReport = rows.get(3)?.report

    const newFinancialReport = {
      milestone_id: milestone_id,
      gfch_share_annual_value: DataGridUtils.formatFieldValue(gfchShareReport),
      share_responsible_organization_annual_value: DataGridUtils.formatFieldValue(shareResponsibleOrganizationReport),
      other_funding_annual_value: DataGridUtils.formatFieldValue(otherFundingReport),
    }

    const { data, error } = await urqlClient
      .mutation<{
        insert_project_financial_report: Mutation_Root['insert_project_financial_report']
      }>(upsertProjectFinancialReportMutation, {
        projectFinancialReport: newFinancialReport,
      })
      .toPromise()

    if (error || data?.insert_project_financial_report?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      if (onSave) {
        notificationService.changesSaved()
        setDirty(false)
        onSave()
      }
    }
  }

  return (
    <Form
      onSubmit={submit(onSaveLocal)}
      render={({ handleSubmit }) => (
        <form
          id="edit-form"
          onSubmit={handleSubmit}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
            }
          }}
        >
          <DataGridContainer>
            <S.DataGrid.TotalRow
              rows={projectFundsRows}
              columns={columns}
              hideFooter
              apiRef={apiRef as any}
              localeText={gridTranslations}
              disableColumnReorder
              disableRowSelectionOnClick
              disableColumnSelector
              onCellEditStop={() => setDirty(true)}
              isCellEditable={({ row }: { row: any }) => row.id !== TOTAL_ROW_ID}
            />
          </DataGridContainer>
        </form>
      )}
    />
  )
}
